import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  Checkbox,
  ListItemButton,
  ListItemText,
  List,
  ListItem,
  TextField,
  Divider
} from '@mui/material';
import { 
  Print as PrintIcon,
  Settings as SettingsIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { 
  DesignIcon, 
  LibraryAddIcon, 
  LocalPrintshopIcon 
} from '../../../components/icons/CustomIcons';
import StoreLayout from '../../../layouts/StoreLayout';
import LabelDesigner, { LabelTemplate, LabelElement } from '../../../components/labels/LabelDesigner';
import PrinterManager, { Printer } from '../../../components/labels/PrinterManager';
import TemplateLibrary from '../../../components/labels/TemplateLibrary';
import { 
  getAllProducts,
  Product 
} from '../../../firebase/services/productService';
import { db } from '../../../firebase/config';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  deleteDoc,
  addDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { printService } from '../../../printServices/PrintService';
import PrintAgentManager from '../../../components/PrintAgentManager';

// Helper function to safely format prices
const formatPrice = (price: any): string => {
  if (price === undefined || price === null) {
    return 'N/A';
  }
  
  if (typeof price !== 'number' || isNaN(price)) {
    return 'N/A';
  }
  
  try {
    return price.toFixed(2);
  } catch (error) {
    console.error('Error formatting price:', error);
    return 'N/A';
  }
};

// Tab options
interface TabOption {
  value: string;
  label: string;
  icon: React.ReactElement;
}

const tabOptions: TabOption[] = [
  { value: 'templates', label: 'Templates', icon: <LibraryAddIcon /> },
  { value: 'design', label: 'Designer', icon: <DesignIcon /> },
  { value: 'printers', label: 'Printers', icon: <LocalPrintshopIcon /> },
  { value: 'agents', label: 'Print Agents', icon: <SettingsIcon /> },
  { value: 'print', label: 'Print Labels', icon: <PrintIcon /> }
];

const LabelsPage: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<LabelTemplate | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState<() => void>(() => {});
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [printLoading, setPrintLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Default Label Templates
  const defaultTemplates: LabelTemplate[] = [
    {
      id: 'template_shelf_standard',
      name: 'Standard Shelf Label',
      type: 'shelf',
      width: 3.5,
      height: 1.5,
      elements: [
        {
          id: 'title',
          type: 'text',
          x: 10,
          y: 10,
          width: 320,
          height: 30,
          content: 'Product Name',
          fontSize: 14,
          fontWeight: 'bold',
          fontFamily: 'Arial',
          color: '#000000',
          dataSource: 'product.name',
          textAlign: 'center'
        },
        {
          id: 'price',
          type: 'text',
          x: 10,
          y: 50,
          width: 320,
          height: 50,
          content: '$19.99',
          fontSize: 24,
          fontWeight: 'bold',
          fontFamily: 'Arial',
          color: '#0066cc',
          dataSource: 'product.price',
          textAlign: 'center'
        },
        {
          id: 'sku',
          type: 'text',
          x: 10,
          y: 110,
          width: 150,
          height: 20,
          content: 'SKU: 12345',
          fontSize: 10,
          fontFamily: 'Arial',
          color: '#666666',
          dataSource: 'product.sku',
          textAlign: 'left'
        }
      ],
      isDefault: true
    },
    {
      id: 'template_barcode_standard',
      name: 'Standard Barcode Label',
      type: 'barcode',
      width: 2,
      height: 1,
      elements: [
        {
          id: 'name',
          type: 'text',
          x: 5,
          y: 5,
          width: 190,
          height: 20,
          content: 'Product Name',
          fontSize: 10,
          fontFamily: 'Arial',
          color: '#000000',
          dataSource: 'product.name',
          textAlign: 'center'
        },
        {
          id: 'barcode',
          type: 'barcode',
          x: 45,
          y: 30,
          width: 100,
          height: 40,
          content: '12345',
          dataSource: 'product.sku'
        },
        {
          id: 'price',
          type: 'text',
          x: 5,
          y: 75,
          width: 190,
          height: 20,
          content: '$19.99',
          fontSize: 12,
          fontWeight: 'bold',
          fontFamily: 'Arial',
          color: '#000000',
          dataSource: 'product.price',
          textAlign: 'center'
        }
      ],
      isDefault: true
    }
  ];

  // Default Printers
  const defaultPrinters: Printer[] = [];

  // Load templates, printers, and products from Firebase
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Load templates
        const templatesSnapshot = await getDocs(collection(db, 'labelTemplates'));
        
        if (templatesSnapshot.empty) {
          // If no templates exist, create default templates
          await Promise.all(defaultTemplates.map(template => 
            setDoc(doc(db, 'labelTemplates', template.id), {
              ...template,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            })
          ));
          setTemplates(defaultTemplates);
        } else {
          const loadedTemplates = templatesSnapshot.docs.map(doc => doc.data() as LabelTemplate);
          setTemplates(loadedTemplates);
        }
        
        // Load printers
        const printersSnapshot = await getDocs(collection(db, 'labelPrinters'));
        
        if (printersSnapshot.empty) {
          // No default printer is created - user must add printers manually
          setPrinters([]);
          
          // Check for print agent printers to suggest synchronization
          await checkPrintAgentPrinters();
        } else {
          const loadedPrinters = printersSnapshot.docs.map(doc => {
            const printerData = doc.data() as Printer;
            // Ensure address is never undefined when loading from Firestore
            return {
              ...printerData,
              address: printerData.address || '',
              port: printerData.port || 9100
            };
          });
          
          // Log loaded printers for debugging
          console.log('Loaded printers from Firestore:', loadedPrinters);
          
          setPrinters(loadedPrinters);
        }
        
        // Load products
        const productsData = await getAllProducts();
        setProducts(productsData);
        
      } catch (err) {
        console.error('Error loading label data:', err);
        setError('Failed to load label data. Please try again.');
      } finally {
    setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Check for print agent printers
  const checkPrintAgentPrinters = async () => {
    try {
      // Check if print agent is available
      const agentConfig = printService.getPrintAgentConfig();
      await printService.checkLocalPrintAgent(agentConfig.hostname, agentConfig.port);
      
      // Get printers from print agent
      const agentPrintersRaw = localStorage.getItem('print_agent_printers');
      if (agentPrintersRaw) {
        const agentPrinters = JSON.parse(agentPrintersRaw);
        if (agentPrinters.length > 0) {
          // Show notification that printers are available to sync
          setSuccess(`${agentPrinters.length} printer(s) detected in Print Agent. Go to Printers tab to sync them with TendCloud.`);
        }
      }
    } catch (error) {
      console.log('Print agent check failed:', error);
      // This is non-critical, so we don't need to show an error
    }
  };

  // Handle changing the active tab
  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    // Check if we need to prompt the user about unsaved changes
    if (activeTab === 'design' && currentTemplate) {
      // For simplicity, we're just switching tabs without checking for unsaved changes
      // In a real app, you might want to add a check here
    }
    
    setActiveTab(newValue);
  };

  // Handle creating a new template
  const handleCreateTemplate = () => {
    const newTemplate: LabelTemplate = {
      id: `template_${Date.now()}`,
      name: 'New Template',
      type: 'custom',
      width: 3,
      height: 2,
      elements: []
    };
    
    setCurrentTemplate(newTemplate);
    setActiveTab('design');
  };

  // Handle selecting a template to edit
  const handleSelectTemplate = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setCurrentTemplate(template);
      setActiveTab('design');
    }
  };

  // Handle duplicating a template
  const handleDuplicateTemplate = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const newTemplate: LabelTemplate = {
        ...template,
        id: `template_${Date.now()}`,
        name: `${template.name} (Copy)`,
        isDefault: false
      };
      
      // Save to Firestore
      setDoc(doc(db, 'labelTemplates', newTemplate.id), {
        ...newTemplate,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      .then(() => {
        setTemplates([...templates, newTemplate]);
        setSuccess('Template duplicated successfully!');
      })
      .catch(err => {
        console.error('Error duplicating template:', err);
        setError('Failed to duplicate template. Please try again.');
      });
    }
  };

  // Handle saving a template
  const handleSaveTemplate = (template: LabelTemplate) => {
    // Update or add the template
    const isNewTemplate = !templates.some(t => t.id === template.id);
    
    // Save to Firestore
    setDoc(doc(db, 'labelTemplates', template.id), {
      ...template,
      updatedAt: serverTimestamp(),
      ...(isNewTemplate ? { createdAt: serverTimestamp() } : {})
    })
    .then(() => {
      if (isNewTemplate) {
        setTemplates([...templates, template]);
      } else {
        setTemplates(templates.map(t => t.id === template.id ? template : t));
      }
      setCurrentTemplate(template);
      setSuccess('Template saved successfully!');
    })
    .catch(err => {
      console.error('Error saving template:', err);
      setError('Failed to save template. Please try again.');
    });
  };

  // Handle deleting a template
  const handleDeleteTemplate = (templateId: string) => {
    // Check if it's a default template
    const template = templates.find(t => t.id === templateId);
    if (template?.isDefault) {
      setError('Cannot delete a default template.');
      return;
    }
    
    // Delete from Firestore
    deleteDoc(doc(db, 'labelTemplates', templateId))
    .then(() => {
      setTemplates(templates.filter(t => t.id !== templateId));
      
      // If the current template is being deleted, clear it
      if (currentTemplate?.id === templateId) {
        setCurrentTemplate(null);
      }
      
      setSuccess('Template deleted successfully!');
    })
    .catch(err => {
      console.error('Error deleting template:', err);
      setError('Failed to delete template. Please try again.');
    });
  };

  // Handle adding a printer
  const handleAddPrinter = (printer: Printer) => {
    // Save to Firestore
    setDoc(doc(db, 'labelPrinters', printer.id), {
      ...printer,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    .then(() => {
      setPrinters([...printers, printer]);
      setSuccess('Printer added successfully!');
    })
    .catch(err => {
      console.error('Error adding printer:', err);
      setError('Failed to add printer. Please try again.');
    });
  };

  // Handle updating a printer
  const handleUpdatePrinter = (printer: Printer) => {
    // Ensure the printer object has the correct address field
    const printerToUpdate = {
      ...printer,
      address: printer.address || '', // Make sure address is never undefined
      port: printer.port || 9100
    };
    
    // Log the printer update for debugging
    console.log('Updating printer in Firestore:', printerToUpdate);
    
    // Update in Firestore
    updateDoc(doc(db, 'labelPrinters', printer.id), {
      ...printerToUpdate,
      updatedAt: serverTimestamp()
    })
    .then(() => {
      setPrinters(printers.map(p => p.id === printer.id ? printerToUpdate : p));
      setSuccess('Printer updated successfully!');
    })
    .catch(err => {
      console.error('Error updating printer:', err);
      setError('Failed to update printer. Please try again.');
    });
  };

  // Handle deleting a printer
  const handleDeletePrinter = (printerId: string) => {
    // Check if it's the default printer
    const printer = printers.find(p => p.id === printerId);
    if (printer?.isDefault) {
      setError('Cannot delete the default printer.');
      return;
    }
    
    // Delete from Firestore
    deleteDoc(doc(db, 'labelPrinters', printerId))
    .then(() => {
      setPrinters(printers.filter(p => p.id !== printerId));
      setSuccess('Printer deleted successfully!');
    })
    .catch(err => {
      console.error('Error deleting printer:', err);
      setError('Failed to delete printer. Please try again.');
    });
  };

  // Handle setting a default printer
  const handleSetDefaultPrinter = (printerId: string) => {
    // Update all printers to set the selected one as default
    const updatedPrinters = printers.map(p => ({
      ...p,
      isDefault: p.id === printerId
    }));
    
    // Update in Firestore (batch update would be better for production)
    Promise.all(updatedPrinters.map(printer => 
      updateDoc(doc(db, 'labelPrinters', printer.id), {
        isDefault: printer.isDefault,
        updatedAt: serverTimestamp()
      })
    ))
    .then(() => {
      setPrinters(updatedPrinters);
      setSuccess('Default printer set successfully!');
    })
    .catch(err => {
      console.error('Error setting default printer:', err);
      setError('Failed to set default printer. Please try again.');
    });
  };

  // Handle printing a template
  const handlePrintTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setPrintDialogOpen(true);
  };

  // Handle printing labels
  const handlePrintLabels = async () => {
    if (!selectedTemplateId || selectedProductIds.length === 0) {
      setError('Please select a template and at least one product to print labels.');
      return;
    }
    
    setPrintLoading(true);
    
    try {
      // Find the selected template
      const template = templates.find(t => t.id === selectedTemplateId);
      if (!template) {
        throw new Error('Template not found');
      }

      // Find the default printer
      const defaultPrinter = printers.find(p => p.isDefault);
      if (!defaultPrinter) {
        if (printers.length === 0) {
          throw new Error('No printers configured. Please add a printer first.');
        } else {
          throw new Error('No default printer selected. Please set a default printer.');
        }
      }

      // Get the selected products
      const selectedProducts = products.filter(p => selectedProductIds.includes(p.id));
      
      // Print the labels using the print service
      const printResult = await printService.printLabel(
        defaultPrinter,
        template,
        selectedProducts,
        1 // One copy per product
      );
      
      if (printResult.success) {
        setSuccess(`Printed ${selectedProducts.length} labels successfully!`);
      } else {
        setError(`Failed to print labels: ${printResult.message}`);
      }
    } catch (error) {
      console.error('Print error:', error);
      setError(`Failed to print labels: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setPrintLoading(false);
      setPrintDialogOpen(false);
    }
  };

  // Handle testing a printer
  const handleTestPrinter = async (printerId: string): Promise<boolean> => {
    // Find the printer to test
    const printer = printers.find(p => p.id === printerId);
    if (!printer) return false;
    
    try {
      // Use our print service to test the printer
      return await printService.testPrinter(printer);
    } catch (error) {
      console.error('Test printer error:', error);
      return false;
    }
  };

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'templates':
        return (
          <TemplateLibrary
            templates={templates}
            onSelectTemplate={handleSelectTemplate}
            onCreateTemplate={handleCreateTemplate}
            onDuplicateTemplate={handleDuplicateTemplate}
            onDeleteTemplate={handleDeleteTemplate}
            onPrintTemplate={handlePrintTemplate}
          />
        );
        
      case 'design':
        return currentTemplate ? (
          <LabelDesigner
            template={currentTemplate}
            onSave={handleSaveTemplate}
            onDelete={
              currentTemplate.isDefault ? undefined : 
              () => handleDeleteTemplate(currentTemplate.id)
            }
          />
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Select a template to edit or create a new one
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateTemplate}
              sx={{ mt: 2 }}
            >
              Create New Template
            </Button>
          </Paper>
        );
        
      case 'printers':
        return (
          <PrinterManager
            printers={printers}
            onAddPrinter={handleAddPrinter}
            onUpdatePrinter={handleUpdatePrinter}
            onDeletePrinter={handleDeletePrinter}
            onSetDefaultPrinter={handleSetDefaultPrinter}
            onTestPrinter={handleTestPrinter}
          />
        );
        
      case 'agents':
        return <PrintAgentManager />;
        
      case 'print':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Print Labels
            </Typography>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Select a template from the Templates tab to print labels for products.
              </Typography>
              <Button
                variant="contained"
                startIcon={<LibraryAddIcon />}
                onClick={() => setActiveTab('templates')}
                sx={{ mb: 3 }}
              >
                Go to Templates
              </Button>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Print Agent
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                For the best printing experience, install the Print Agent application.
                It automatically discovers your printers and keeps them in sync with the FLL Marina system.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                The new outbound-only print agent works on any network without port forwarding requirements.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  href="/downloads/print-agent/macos"
                  download
                >
                  Download for macOS
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  href="/downloads/print-agent/windows"
                  download
                >
                  Download for Windows
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  href="/downloads/print-agent/linux"
                  download
                >
                  Download for Linux
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Version 1.0.0 | Last updated: {new Date().toLocaleDateString()}
              </Typography>
            </Paper>
          </Box>
        );
        
      default:
        return null;
    }
  };

  // Render product selection list for printing
  const renderProductSelection = () => {
    // Filter products based on search query
    const filteredProducts = products.filter(p => 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.sku && p.sku.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    
    const handleToggleProduct = (productId: string) => {
      setSelectedProductIds(ids => 
        ids.includes(productId) 
          ? ids.filter(id => id !== productId)
          : [...ids, productId]
      );
    };
    
    return (
      <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
          label="Search Products"
          variant="outlined"
              size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
        />
        
        {filteredProducts.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center">
            No products found
          </Typography>
        ) : (
          <List dense sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #eee' }}>
            {filteredProducts.map(product => (
              <ListItem
                key={product.id}
                dense
                disablePadding
                secondaryAction={
                  <Checkbox
                    edge="end"
                    checked={selectedProductIds.includes(product.id)}
                    onChange={() => handleToggleProduct(product.id)}
                  />
                }
              >
                <ListItemButton onClick={() => handleToggleProduct(product.id)}>
                  <ListItemText
                    primary={product.name}
                    secondary={`SKU: ${product.sku || 'N/A'} - Price: $${formatPrice(product.price)}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
        
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            {selectedProductIds.length} products selected
          </Typography>
          
          <Button
            size="small"
            onClick={() => setSelectedProductIds(filteredProducts.map(p => p.id))}
            disabled={filteredProducts.length === 0}
          >
            Select All
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <StoreLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Label Management System
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          {tabOptions.map(option => (
            <Tab
              key={option.value}
              value={option.value}
              label={option.label}
              icon={option.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderTabContent()
        )}
        
        {/* Success Snackbar */}
        <Snackbar
          open={!!success}
          autoHideDuration={4000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
        
        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
        >
          <DialogTitle>Confirm Action</DialogTitle>
          <DialogContent>
            {confirmDialogMessage}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                confirmDialogAction();
                setConfirmDialogOpen(false);
              }}
              color="primary"
              variant="contained"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Print Dialog */}
      <Dialog
          open={printDialogOpen}
          onClose={() => setPrintDialogOpen(false)}
          maxWidth="md"
        fullWidth
      >
          <DialogTitle>Print Labels</DialogTitle>
        <DialogContent>
            <Typography variant="subtitle1">
              Selected Template: {templates.find(t => t.id === selectedTemplateId)?.name || 'None'}
            </Typography>
            
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Select Products to Print Labels For:
            </Typography>
            
            {renderProductSelection()}
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setPrintDialogOpen(false)}>Cancel</Button>
          <Button 
              onClick={handlePrintLabels}
              color="primary"
            variant="contained" 
              startIcon={printLoading ? <CircularProgress size={20} /> : <PrintIcon />}
              disabled={printLoading || selectedProductIds.length === 0}
            >
              {printLoading ? 'Printing...' : 'Print Labels'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </StoreLayout>
  );
};

export default LabelsPage; 